import { useState, useEffect, useCallback } from 'react';
import circleService from '../circleService';
import notificationService from '../notificationService';

/**
 * React hook for using the notification service
 * @param {number} userId - The user ID
 * @returns {Object} Hook API
 */
export const useNotificationService = (userId) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [circleMembers, setCircleMembers] = useState([]);

  // Function to fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Get circle members
      let members = [];
      try {
        members = await circleService.getMembersInSameCircles(userId) || [];
        setCircleMembers(members);
      } catch (err) {
        console.warn('Error fetching circle members:', err);
        // Continue even if we can't get circle members
      }
      
      // Get all notifications
      const allNotifications = await notificationService.getNotifications(userId);
      setNotifications(allNotifications);
      
      // Get unread count (only for current user's notifications)
      const currentUserNotifications = allNotifications.filter(n => n.userId === userId);
      const count = currentUserNotifications.filter(n => !n.isRead).length;
      setUnreadCount(count);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Initialize and handle cleanup
  useEffect(() => {
    if (!userId) return;
    
    // Initialize service
    notificationService.initialize(userId);
    
    // Fetch initial notifications
    fetchNotifications();
    
    // Set up listener for changes
    const handleNotificationsChange = () => {
      fetchNotifications();
    };
    
    notificationService.addListener(handleNotificationsChange);
    
    // Cleanup on unmount
    return () => {
      notificationService.removeListener(handleNotificationsChange);
    };
  }, [userId, fetchNotifications]);

  // Return the hook API
  return {
    unreadCount,
    notifications,
    circleMembers,
    loading,
    error,
    refreshNotifications: fetchNotifications,
    markAsRead: notificationService.markAsRead.bind(notificationService),
    deleteNotification: notificationService.deleteNotification.bind(notificationService),
    recordMedicationTaken: notificationService.recordMedicationTaken.bind(notificationService)
  };
}; 